import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { QrCode, Search, Printer, Smartphone } from 'lucide-react';

export default function QRDisplay() {
  const [protocols, setProtocols] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/health/protocols`).then(r => setProtocols(r.data || [])).catch(() => {});
  }, []);

  const filtered = protocols.filter(p =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    (p.subtitle || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen pt-20 pb-10 px-4 sm:px-6" style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #CAE9FA 40%, #f0fdf4 100%)' }} data-testid="qr-display-page">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6">
        {/* Liste des protocoles */}
        <div className="lg:w-80 flex-shrink-0 print:hidden">
          <div className="flex items-center gap-2 mb-3">
            <QrCode className="w-5 h-5" style={{ color: '#002768' }} />
            <h1 className="text-lg font-bold" style={{ color: '#002768' }}>Codes QR — Guides mobiles</h1>
          </div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Chercher un déterminant…"
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white"
              data-testid="qr-search-input"
            />
          </div>
          <div className="space-y-1 max-h-[65vh] overflow-y-auto pr-1">
            {filtered.map(p => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selected?.id === p.id ? 'text-white font-semibold' : 'bg-white hover:bg-blue-50 text-gray-700'}`}
                style={selected?.id === p.id ? { backgroundColor: '#002768' } : {}}
                data-testid={`qr-protocol-${p.id}`}
              >
                {p.title}
              </button>
            ))}
            {filtered.length === 0 && <p className="text-sm text-gray-500 px-2 py-3">Aucun protocole trouvé.</p>}
          </div>
        </div>

        {/* Affichage grand format */}
        <div className="flex-1 flex items-center justify-center">
          {selected ? (
            <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-10 text-center w-full max-w-2xl" data-testid="qr-big-display">
              <h2 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: '#002768' }}>{selected.title}</h2>
              {selected.subtitle && <p className="text-sm text-gray-500 mb-4">{selected.subtitle}</p>}
              <img
                src={`${API}/qr/guide/${selected.id}?size=16`}
                alt={`QR ${selected.title}`}
                className="mx-auto w-full max-w-[420px] aspect-square"
                data-testid="qr-image"
              />
              <div className="flex items-center justify-center gap-2 mt-4 text-gray-600">
                <Smartphone className="w-4 h-4" />
                <span className="text-sm font-medium">simulateur911.ca/m/guide/{selected.id}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Scannez avec la caméra de votre téléphone — guide mobile + auto-évaluation</p>
              <button
                onClick={() => window.print()}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium print:hidden"
                style={{ backgroundColor: '#002768' }}
                data-testid="qr-print-btn"
              >
                <Printer className="w-4 h-4" /> Imprimer
              </button>
            </div>
          ) : (
            <div className="text-center text-gray-500 print:hidden" data-testid="qr-empty-state">
              <QrCode className="w-16 h-16 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Choisissez un déterminant à gauche</p>
              <p className="text-sm">Le code QR s'affichera en grand format, prêt à projeter en classe.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
